import { Link } from 'react-router-dom';
import { Sparkles, Check, Star, Zap, Crown, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { PromoBadge } from '@/components/PromoBanner';

const PACKAGES = [
  { id: 'starter', icon: Star, name: 'Starter', credits: 5, usd: 5, inr: 400, bonus: 0, tagline: 'Try the AI features' },
  { id: 'pro', icon: Zap, name: 'Pro', credits: 22, usd: 20, inr: 1600, bonus: 2, tagline: '2 bonus credits included', popular: true },
  { id: 'premium', icon: Crown, name: 'Premium', credits: 60, usd: 50, inr: 4000, bonus: 10, tagline: '10 bonus credits included' },
];

const FEATURES = [
  { name: 'Browse 127+ products across regions', free: true },
  { name: '32 DIY rituals + daily tips', free: true },
  { name: 'Weather-based recommendations', free: true },
  { name: 'Community reviews feed', free: true },
  { name: 'Favorites & beauty profile', free: true },
  { name: 'AI Q&A (1 credit per question)', free: false },
  { name: 'AI Photo Styling (2 credits)', free: false },
  { name: 'AI Regimen Generation (2 credits)', free: false },
  { name: '3 free welcome credits on signup', free: true },
];

export default function PricingPage() {
  const { user, promo } = useAuth();
  return (
    <div data-testid="pricing-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Pricing
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Simple, credit-based pricing.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Browsing, community reviews and weather recommendations are always free. Pay only when you
          use our AI features. 1 credit = <strong>$1 USD</strong> or <strong>₹80 INR</strong>. Credits never expire.
        </p>
        {promo?.active && (
          <div className="mt-4" data-testid="pricing-promo-callout">
            <PromoBadge />
            <p className="mt-2 text-sm text-foreground/80">
              All AI features are <strong>completely free</strong> while we launch — no credits needed.
              Credits packages below will apply once the promo ends.
            </p>
          </div>
        )}
      </div>

      {/* Packages */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {PACKAGES.map((p) => {
          const Icon = p.icon;
          return (
            <Card
              key={p.id}
              className={`rounded-2xl p-6 border-border/70 relative overflow-hidden transition-all ${
                p.popular ? 'bg-cream-paper border-rose-tint shadow-md' : 'bg-card hover:shadow-md'
              }`}
              data-testid={`pricing-package-${p.id}`}
            >
              {p.popular && (
                <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.14em]">
                  Most popular
                </Badge>
              )}
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-accent/60 border border-rose-tint flex items-center justify-center">
                  <Icon className="h-5 w-5 text-rose-gold" />
                </div>
                <h3 className="font-display text-2xl text-ink">{p.name}</h3>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">{p.tagline}</p>

              <div className="mt-5">
                <p className="font-display text-4xl text-ink tabular-nums">${p.usd}</p>
                <p className="text-sm text-muted-foreground mt-0.5">or ₹{p.inr.toLocaleString('en-IN')}</p>
                <p className="text-sm text-foreground/85 mt-2">
                  <span className="font-semibold tabular-nums">{p.credits}</span> credits
                  {p.bonus > 0 && <span className="ml-1 text-rose-gold font-medium">(+{p.bonus} bonus)</span>}
                </p>
              </div>

              <Button
                asChild
                className={`mt-6 w-full rounded-xl ${
                  p.popular
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-foreground text-background hover:bg-foreground/90'
                }`}
                data-testid={`pricing-cta-${p.id}`}
              >
                <Link to={user ? '/credits' : '/signup'}>
                  {user ? `Buy ${p.name}` : 'Sign up to buy'} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Link>
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Feature matrix */}
      <section className="mt-12">
        <h2 className="font-display text-3xl text-ink">What’s included</h2>
        <p className="mt-1 text-sm text-muted-foreground">Most of Radiance Hub is free. Credits only power AI features.</p>
        <Card className="mt-6 rounded-2xl bg-card border-border/70 divide-y divide-border/60">
          {FEATURES.map((f) => (
            <div key={f.name} className="flex items-center justify-between gap-4 px-5 py-3 text-sm">
              <span className="text-foreground/85">{f.name}</span>
              {f.free ? (
                <Badge className="bg-accent/60 text-foreground border border-rose-tint text-[10px] uppercase tracking-[0.14em]">
                  <Check className="h-3 w-3 mr-1 text-rose-gold" /> Free
                </Badge>
              ) : (
                <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
                  <Sparkles className="h-3 w-3 mr-1 text-rose-gold" /> Credits
                </Badge>
              )}
            </div>
          ))}
        </Card>
      </section>

      {/* FAQ */}
      <section className="mt-12">
        <h2 className="font-display text-3xl text-ink">Frequently asked</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {[
            { q: 'Do credits expire?', a: 'No. Your credits stay on your account until you use them.' },
            { q: 'What happens if the AI fails?', a: 'If our system fails to return a valid response, your credits are automatically refunded.' },
            { q: 'Can I get a refund?', a: 'Credits are non-refundable except in specific cases (duplicate charge, credits not delivered). See our Refund Policy.' },
            { q: 'Which currencies can I pay in?', a: 'INR via UPI, cards, wallets, net banking — and USD via international cards. All payments are processed by Razorpay.' },
          ].map(({ q, a }) => (
            <div key={q} className="rounded-2xl bg-cream-paper border border-border/70 p-5">
              <p className="font-display text-lg text-ink">{q}</p>
              <p className="mt-1 text-sm text-foreground/80 leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </section>

      <p className="mt-12 text-sm text-muted-foreground text-center">
        Questions? Write to{' '}
        <a className="underline underline-offset-4 text-foreground" href="mailto:Webuser@radiancehub.org">Webuser@radiancehub.org</a>.
      </p>
    </div>
  );
}
